'use client';

import { cn } from '@coas/ui/lib';
import { useTranslations } from 'next-intl';
import { AnimatePresence, motion } from 'framer-motion';
import Link from 'next/link';
import { useRouter } from 'next/navigation';
import { useEffect, useMemo, useState } from 'react';
import { api } from 'trpc/react';
import { env } from '~/env';
import { CartCard } from '~/features/CartCard';
import IconCart from '~/shared/assets/icons/cart.svg';
import { LoadingDots } from '~/shared/ui/loading-dots';
import { Price } from '~/shared/ui/price';
import { Spinner } from '~/shared/ui/spinner';

const fade = {
    initial: { opacity: 0, y: 8 },
    animate: { opacity: 1, y: 4 },
    exit: { opacity: 0, y: 0 },
    transition: { duration: 0.2, ease: 'easeOut' as const },
};

export default () => {
    const router = useRouter();
    const t = useTranslations();

    const { data: cartInfo, isFetching, isRefetching, isSuccess } = api.cart.getAll.useQuery();

    const cartItems = cartInfo?.items ?? [];

    const inOrderCount = useMemo(
        () => cartItems.reduce((sum, item) => (item.postponed ? sum : sum + 1), 0),
        [cartItems],
    );

    const total = cartInfo?.total ?? 0;

    const originalTotal = cartInfo?.totalOriginal;

    const hasDiscount = typeof originalTotal === 'number' && originalTotal > total;

    const [showLoader, setShowLoader] = useState(isRefetching);
    useEffect(() => {
        if (isRefetching) {
            const id = window.setTimeout(() => setShowLoader(true), 120);
            return () => window.clearTimeout(id);
        }
        setShowLoader(false);
    }, [isRefetching]);

    if (isFetching && !isRefetching) {
        return (
            <div className={'absolute inset-page m-auto flex size-fit items-center'}>
                <Spinner className={'size-12'} />
            </div>
        );
    }

    return (
        <>
            {isSuccess && cartItems.length === 0 && (
                <div
                    className={
                        'absolute inset-page -top-8 mx-auto my-auto flex h-fit max-w-[28rem] flex-col items-center gap-y-6 text-balance'
                    }
                >
                    <IconCart className={'w-14 stroke-neutral-950'} />
                    <div className={'flex flex-col gap-y-2'}>
                        <span className={'text-center text-20 font-600 leading-tight'}>{t('cart.empty')}</span>
                    </div>
                    <Link
                        href={'/products'}
                        className={
                            'w-full bg-neutral-950 py-2 text-center text-16 font-600 uppercase text-neutral-50 outline-2 outline-offset-2 outline-neutral-950 focus-visible:outline'
                        }
                    >
                        {t('cart.goShopping')}
                    </Link>
                </div>
            )}
            {isSuccess && cartItems.length > 0 && (
                <div className={'flex w-full flex-1 flex-col gap-y-4'}>
                    <div>
                        <span className={'text-32 font-600'}>
                            <span className={'font-600'}>{t('cart.title')}</span>{' '}
                            <span className={'text-28 font-400 text-neutral-400'}>({cartItems.length})</span>
                        </span>
                    </div>
                    {cartItems.map(cartItem => (
                        <CartCard key={cartItem.productPriceId} cartItem={cartItem} interactive />
                    ))}
                    <div className={'flex-1'} />

                    <div className='sticky bottom-0 z-20 w-full bg-white'>
                        <div className='flex w-full flex-col gap-y-2 py-4'>
                            <span className='flex flex-col items-end self-end leading-tight'>
                                <span className='text-16 font-500 uppercase text-neutral-400'>{t('cart.total')}</span>

                                <div className='relative h-9 w-full min-w-[6rem]'>
                                    <AnimatePresence mode='wait' initial={false}>
                                        {showLoader ? (
                                            <motion.div
                                                key='total-loader'
                                                {...fade}
                                                className='absolute inset-0 flex items-center translate-y-[4px] justify-end'
                                                aria-hidden
                                            >
                                                <LoadingDots />
                                            </motion.div>
                                        ) : (
                                            <motion.div
                                                key={`total-${total}-${hasDiscount ? originalTotal : 'n'}`}
                                                {...fade}
                                                className='flex items-end justify-end gap-2'
                                            >
                                                <Price className='text-32 font-600 leading-none text-neutral-950'>
                                                    {total}
                                                </Price>
                                                {hasDiscount && (
                                                    <Price className='text-20 font-600 leading-none text-red-600 line-through decoration-2 decoration-red-500 -translate-y-1'>
                                                        {originalTotal}
                                                    </Price>
                                                )}
                                            </motion.div>
                                        )}
                                    </AnimatePresence>
                                </div>
                            </span>

                            {env.NEXT_PUBLIC_PAYMENTS_PROBLEM ? (
                                <span
                                    className={cn(
                                        'w-full bg-neutral-200 p-3.5 text-center text-16 font-600 uppercase text-neutral-950',
                                        'outline outline-2 -outline-offset-2 outline-neutral-950',
                                        'aria-disabled:pointer-events-none',
                                    )}
                                >
                                    <span>{t('cart.paymentsUnavailable')}</span>
                                    <a
                                        href={`https://t.me/${env.NEXT_PUBLIC_TELEGRAM_SUPPORT}`}
                                        target='_blank'
                                        className='text-sanity-accent'
                                        rel='noreferrer'
                                    >
                                        @{env.NEXT_PUBLIC_TELEGRAM_SUPPORT}
                                    </a>
                                </span>
                            ) : (
                                <button
                                    type='button'
                                    disabled={inOrderCount === 0}
                                    onClick={() => router.push('/cart/order')}
                                    className={cn(
                                        'w-full py-3.5 text-center text-16 font-600 uppercase outline-2 outline-offset-2 focus-visible:outline',
                                        'disabled:cursor-not-allowed',
                                        inOrderCount === 0
                                            ? 'bg-neutral-600 text-neutral-100 outline-neutral-500'
                                            : 'bg-neutral-950 text-neutral-50 outline-neutral-950',
                                    )}
                                >
                                    {inOrderCount === 0 ? t('cart.next') : `${t('cart.next')} (${inOrderCount})`}
                                </button>
                            )}
                        </div>
                    </div>
                </div>
            )}
        </>
    );
};
